import { fullPublisher, publisher, releasedEvent } from '@/constants/seo'
import { NextSeoPropsWithRequiredFields } from '@/layout/Seo'
import { MovieJsonLdProps } from '@/layout/Seo/JsonLd'
import { TVSeriesJsonLdProps } from '@/layout/Seo/JsonLd/TVSeriesJsonLd'
import { Person } from '@/layout/Seo/JsonLd/types'
import { MediaMetaDataParts } from '@/layout/Seo/SeoHelper'
import { CatalogMovie, CatalogSeries } from '@/types/generated-content-catalog-types'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { Maybe } from '@/utils/types'
import { formatCanonicalUrl } from '@/utils/url'
import { ProjectMeta } from '../ProjectsService/types'
import {
  filterTitleDirector,
  filterTitleProducer,
  formatTitleDuration,
  formatTitleRating,
  formatTitleTitleArtImagePath,
  isSeason,
  isSeries,
  isSeriesCast,
} from './contentCatalogService'
import { CatalogCast, CatalogCrew, CatalogExternalId, CatalogTitle } from './types'

interface SeoPropsInput {
  path: string
  title: string
  catalogTitle: Maybe<CatalogTitle>
  locale: string
  projectMeta?: ProjectMeta
}
/**
 * Preps input props for next-seo, which allows attributes available in
 * the Open Graph protocol, https://ogp.me/
 */
export function mapSeoProps({
  path,
  title,
  catalogTitle,
  locale,
  projectMeta,
}: SeoPropsInput): NextSeoPropsWithRequiredFields {
  const description = catalogTitle
    ? formatDescription(catalogTitle)
    : projectMeta?.showOverview ??
      'Get free unlimited access to film and TV series that amplify light through high quality fan-owned entertainment.'
  const imageUrl = catalogTitle
    ? formatImageUrl(catalogTitle)
    : projectMeta?.discoveryPosterLandscapeCloudinaryPath
    ? getCloudinaryImageUrl({ path: projectMeta.discoveryPosterLandscapeCloudinaryPath, width: 1200, height: 630 })
    : getCloudinaryImageUrl({ path: 'v1756153598/angel-studios/seo/angel-opengraph-5', width: 1200, height: 630 })

  return {
    canonical: formatCanonicalUrl({ locale, path }),
    description,
    openGraph: {
      description,
      type: `video.${formatOpenGraphType(catalogTitle)}`,
      images: [
        {
          url: imageUrl,
          width: 1200,
          height: 630,
          alt: description,
        },
      ],
      site_name: 'Angel',
      title,
      url: formatCanonicalUrl({ locale, path }),
    },
    title,
    additionalMetaTags: [
      {
        property: 'twitter:image',
        content: imageUrl,
      },
    ],
  }
}

type JsonLdLike = MovieJsonLdProps & TVSeriesJsonLdProps
interface JsonLdInput {
  path: string
  catalogTitle: CatalogMovie | CatalogSeries
  locale: string
}
export function mapJsonLdProps({ path, catalogTitle, locale }: JsonLdInput): JsonLdLike {
  return {
    actor: formatActors(catalogTitle),
    contentRating: formatTitleRating(catalogTitle),
    dateCreated: new Date(catalogTitle.originalReleaseDate).toISOString(),
    description: formatDescription(catalogTitle),
    director: filterTitleDirector(catalogTitle).map(formatCrewPerson),
    duration: formatDurationIso8601(catalogTitle),
    genre: formatGenres(catalogTitle),
    image: formatImageUrl(catalogTitle),
    name: catalogTitle?.title,
    producer: filterTitleProducer(catalogTitle).map(formatCrewPerson),
    publisher,
    releasedEvent,
    sameAs: formatSameAsUrls(catalogTitle),
    url: formatCanonicalUrl({ locale, path }),
  }
}

export function formatSameAsUrls(catalogTitle: CatalogTitle): string[] {
  const formatters: { [source: string]: (externalId: CatalogExternalId) => string } = {
    rotten_tomatoes: formatRottenTomatoesUrl.bind(null, catalogTitle),
    imdb: formatImdbUrl,
    wikipedia: formatWikipediaUrl,
  }
  const supportedSources = Object.keys(formatters)
  const externalIds = catalogTitle?.externalIds.filter((id) => supportedSources.includes(id.source))
  return externalIds
    .map((externalId) => {
      const formatter = formatters[externalId.source]
      if (!formatter) {
        logger().warn(`No formatter found for external ID source: ${externalId.source}`)
        return null
      }
      return formatter(externalId)
    })
    .filter(isDefined)
}

function formatRottenTomatoesUrl(catalogTitle: CatalogTitle, externalId: CatalogExternalId): string {
  return `https://www.rottentomatoes.com/${isSeries(catalogTitle) ? 'tv' : 'm'}/${externalId.id}`
}

function formatImdbUrl(externalId: CatalogExternalId): string {
  return `https://www.imdb.com/title/${externalId.id}`
}

function formatWikipediaUrl(externalId: CatalogExternalId): string {
  return `https://wikipedia.org/wiki/${externalId.id}`
}

function formatOpenGraphType(catalogTitle: Maybe<CatalogTitle>): MediaMetaDataParts['type'] {
  return catalogTitle ? (isSeries(catalogTitle) ? 'tv_show' : 'movie') : 'other'
}

function formatDescription(catalogTitle?: CatalogTitle): string {
  return catalogTitle?.description.short || (fullPublisher.slogan as string)
}

function formatImageUrl(catalogTitle?: CatalogTitle): string {
  const path = catalogTitle && formatTitleTitleArtImagePath(catalogTitle)
  return path ? getCloudinaryImageUrl({ path, width: 1200, height: 630 }) : (fullPublisher.logo as string)
}

export function formatActors(catalogTitle?: CatalogTitle): Person[] {
  return !catalogTitle || isSeason(catalogTitle) ? [] : catalogTitle?.cast.map(formatCastPerson) || []
}

function formatCastPerson(cast: CatalogCast): Person {
  return {
    type: 'Person',
    name: cast.name,
    character: {
      type: 'Person',
      name: isSeriesCast(cast) ? cast.characters[0] : cast.character,
    },
  }
}

function formatCrewPerson(cast: CatalogCrew): Person {
  return {
    type: 'Person',
    name: cast.name,
  }
}

export function formatGenres(catalogTitle?: CatalogTitle): string[] {
  return !catalogTitle || isSeason(catalogTitle) ? [] : catalogTitle?.genres
}

/**
 * @see https://schema.org/Duration
 * @see https://en.wikipedia.org/wiki/ISO_8601
 */
function formatDurationIso8601(catalogTitle?: CatalogTitle): string | undefined {
  if (!catalogTitle) return undefined
  const partialFormat = formatTitleDuration(catalogTitle)

  return partialFormat && 'PT' + partialFormat
}
